import React, { useState, useEffect } from "react";
import { API_URL, API_URL_STORE } from "../components/config";
import { useParams } from "react-router-dom"; // Import useParams

const CURRENCIES = [
  { code: "USD", name: "US Dollar", flag: "🇺🇸" },
  { code: "EUR", name: "Euro", flag: "🇪🇺" },
  { code: "GBP", name: "British Pound", flag: "🇬🇧" },
  { code: "JPY", name: "Japanese Yen", flag: "🇯🇵" },
  { code: "INR", name: "Indian Rupee", flag: "🇮🇳" },
];

const User = () => {
  const { userId } = useParams(); // Get userId from URL params
  const [history, setTopUpHistory] = useState([]);
  const [date, setDate] = useState(new Date().toISOString());
  const [balance, setBalance] = useState(0);
  const [debit, setDebit] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Get balance
  const getUserBalances = async () => {
    try {
      const resp = await fetch(`${API_URL}/topUp/userBalance/${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) {
        throw new Error(`HTTP error! status: ${resp.status}`);
      }

      const result = await resp.json();
      return result.data[0].user_wallet_balance;
    } catch (error) {
      console.error("Error fetching user balances:", error);
      alert(
        "An error occurred while fetching user balances. Please check the console for details."
      );
      return 0;
    }
  };

  // Get total balances
  const getTotalBalances = async () => {
    try {
      const resp = await fetch(
        `${API_URL}/topUp/get_all_entity_total_balances`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!resp.ok) {
        throw new Error(`HTTP error! status: ${resp.status}`);
      }

      const result = await resp.json();
      return result.data[2][0].main_wallet_balance;
    } catch (error) {
      console.error("Error fetching balances:", error);
      alert(
        "An error occurred while fetching balances. Please check the console for details."
      );
      return 0;
    }
  };

  // Top up history
  const fetchTopUpHistory = async () => {
    if (!userId) return;

    try {
      const resp = await fetch(`${API_URL}/topUp/topup/${userId}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!resp.ok) {
        throw new Error(`HTTP error! status: ${resp.status}`);
      }

      const result = await resp.json();
      if (result) {
        const sortedHistory = result.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setTopUpHistory(sortedHistory);
        setBalance(result[0]?.user_wallet_balance || 0);
        setDate(new Date().toISOString());
      } else {
        alert("Failed to fetch History.");
      }
    } catch (error) {
      console.error("Error fetching History:", error);
      alert(
        "An error occurred while fetching History. Please check the console for details."
      );
    }
  };

  useEffect(() => {
    fetchTopUpHistory();
  }, [userId]);

  // Local top up
  const topUplocal = async (e) => {
    e.preventDefault();
    try {
      const userBal = await getUserBalances();
      const total = await getTotalBalances();

      const topupDetails = {
        currency: "USD",
        exchange_rate: 1.0,
        date: date,
        description: "Top Up",
        client_profile_id: userId,
        userId_id: "",
        payment_gateway_id: "",
        main_wallet_id: 1,
        revenue_wallet_id: "",
        total_usage: 0,
        trip_id: "N/A",
        trxn_code: "TU",
        // Backend now derives this from user_wallet_debit if omitted (fixed 2026-09-15, see
        // KNOWN_ISSUES_AND_FIX_BACKLOG.md #7 — this form never sent it, which always 400'd) —
        // sent explicitly here too so the request is self-describing, not relying on a fallback.
        amount: parseFloat(debit),
        user_wallet_debit: parseFloat(debit),
        user_wallet_credit: 0,
        user_wallet_balance: userBal + parseFloat(debit),
        main_wallet_debit: parseFloat(debit),
        main_wallet_credit: 0,
        main_wallet_balance: total + parseFloat(debit),
        folio: "UW",
      };

      const res = await fetch(`${API_URL}/TopUp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(topupDetails),
      });

      if (res.status === 200) {
        alert("Top up successful!");
        fetchTopUpHistory();
      } else {
        alert("Failed to top up. Please try again.");
      }
    } catch (err) {
      console.error("Error during top up:", err.message);
      alert("Error during top up. Please check the console for details.");
    }
  };

  const openModal = () => {
    setIsModalOpen(true);
    setDebit(0); // Reset form when opening
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setDebit(0); // Reset form when closing
  };

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-[#002966] to-[#001433] flex flex-col items-center justify-start relative p-6 space-y-8">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-white/5 rounded-full -top-48 -left-48 blur-3xl"></div>
        <div className="absolute w-96 h-96 bg-white/5 rounded-full -bottom-48 -right-48 blur-3xl"></div>
      </div>

      {/* Top Up Button */}
      <div className="w-full max-w-4xl flex justify-end mb-4">
        <button
          onClick={openModal}
          className="px-6 py-3 rounded-xl font-semibold text-white bg-[#002966] hover:bg-[#003d99] transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl border border-white/20"
        >
          Top Up Balance
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={closeModal}
          ></div>
          
          <div className="relative z-50 w-full max-w-md p-8 bg-white rounded-2xl shadow-2xl transform transition-all">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <form onSubmit={topUplocal} className="space-y-6">
              <h2 className="text-2xl font-bold text-[#002966]">
                Complete Transaction
              </h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="userId" className="block text-sm font-medium text-[#002966]">
                    User ID
                  </label>
                  <input
                    id="userId"
                    value={userId}
                    type="text"
                    readOnly
                    className="mt-1 block w-full rounded-xl py-3 px-4 bg-[#f8fafc] border border-[#002966]/20 focus:border-[#002966] focus:ring-2 focus:ring-[#002966]/20 outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="amount" className="block text-sm font-medium text-[#002966]">
                    Amount
                  </label>
                  <input
                    id="amount"
                    type="number"
                    min="0"
                    step="any"
                    value={debit}
                    onChange={(e) => setDebit(e.target.value)}
                    className="mt-1 block w-full rounded-xl py-3 px-4 bg-[#f8fafc] border border-[#002966]/20 focus:border-[#002966] focus:ring-2 focus:ring-[#002966]/20 outline-none"
                    placeholder="Enter amount"
                  />
                </div>

                <div>
                  <label htmlFor="currency" className="block text-sm font-medium text-[#002966]">
                    Currency
                  </label>
                  <div className="relative mt-1">
                    <select
                      id="currency"
                      className="block w-full rounded-xl py-3 px-4 bg-[#f8fafc] border border-[#002966]/20 focus:border-[#002966] focus:ring-2 focus:ring-[#002966]/20 outline-none appearance-none"
                    >
                      {CURRENCIES.map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.flag} {curr.code} — {curr.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-[#002966]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-3 px-6 rounded-xl font-semibold text-white bg-[#002966] hover:bg-[#003d99] transform transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg"
              >
                Complete Transaction
              </button>
            </form>
          </div>
        </div>
      )}

      {/* History Table */}
      <div className="relative z-10 w-full max-w-4xl bg-white/95 shadow-2xl rounded-2xl overflow-hidden backdrop-blur-lg">
        <h2 className="text-2xl font-bold text-white p-6 bg-[#002966]">
          Top Up History
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#002966]/10">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#002966]">Date</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#002966]">Description</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#002966]">Debit</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#002966]">Credit</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#002966]">Balance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#002966]/10">
              {history.map((item) => (
                <tr key={item.top_up_id} className="hover:bg-[#002966]/5 transition-colors duration-150">
                  <td className="px-6 py-4 text-sm text-[#002966]/80">
                    {new Date(item.date).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#002966]/80">
                    {item.description}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#002966]/80">
                    {item.main_wallet_debit}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#002966]/80">
                    {item.user_wallet_credit}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#002966]/80">
                    {item.user_wallet_balance}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default User;